// src/components/FixtureSelectionList.jsx
import React from "react";
import { Box, Typography, Button } from "@mui/material";
import ManchsterLogo from "../../assets/images/ManchesterLogo.png";
import GrimsbyTownFCLogo from "../../assets/images/GrimsbyTownFCLogo.png";
import { formatDateDDMMYYYY } from "../../utils/fixtureTime";

// On a failed image load (e.g. tricode without a matching S3 badge), swap
// to the local fallback so we don't render the broken-image glyph.
const onImageError = (fallback) => (e) => {
  if (e?.target && e.target.src !== fallback) {
    e.target.src = fallback;
  }
};

const logoSx = {
  width: 36,
  height: 36,
  objectFit: "contain",
  // Soft white halo so light-on-dark crests (Tottenham, Real Madrid etc.)
  // don't disappear into the overlay background.
  filter:
    "drop-shadow(0 0 2px rgba(255,255,255,0.55)) drop-shadow(0 0 6px rgba(255,255,255,0.25))",
};

// Chronological sort key from the fixture's date + UTC kickoff so same-day
const kickoffSortKey = (fx) => {
  if (!fx?.date) return Number.POSITIVE_INFINITY;
  const datePart = String(fx.date).slice(0, 10); // YYYY-MM-DD
  const time = /^\d{2}:\d{2}$/.test(fx.kickoff) ? fx.kickoff : "00:00";
  const ts = new Date(`${datePart}T${time}:00Z`).getTime();
  return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
};

export default function FixtureSelectionList({
  fixtures = [],
  selectedFixtureId,
  onSelect,
}) {
  // Default ordering: earliest kickoff first (non-mutating copy).
  const sortedFixtures = [...fixtures].sort(
    (a, b) => kickoffSortKey(a) - kickoffSortKey(b),
  );

  return (
    <Box
      sx={{
        width: "100%",
        background: "#141414",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {sortedFixtures.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
            No fixtures found for selected filters.
          </Typography>
        </Box>
      )}

      {sortedFixtures.map((fx) => {
        const isActive = selectedFixtureId === fx.id;

        return (
          <Box
            key={fx.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              py: 1.75,
              px: 2.5,
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              background: isActive ? "rgba(39,176,255,0.08)" : "transparent",
              transition: "background 0.15s",
              "&:hover": isActive
                ? undefined
                : { background: "rgba(255,255,255,0.03)" },
            }}
          >
            {/* MATCHUP: logos on the OUTER side, names in the middle.
                Symmetric layout reads as "Home vs Away" left-to-right with
                each crest next to its own team. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                component="img"
                src={fx.team1Logo || ManchsterLogo}
                alt={fx.team1}
                onError={onImageError(ManchsterLogo)}
                sx={logoSx}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  textAlign: "right",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fx.team1}
              </Typography>
              <Typography
                sx={{
                  opacity: 0.5,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                vs
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  textAlign: "left",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fx.team2}
              </Typography>
              <Box
                component="img"
                src={fx.team2Logo || GrimsbyTownFCLogo}
                alt={fx.team2}
                onError={onImageError(GrimsbyTownFCLogo)}
                sx={logoSx}
              />
            </Box>

            {/* KICKOFF: local time with TZ, then date. UTC line dropped —
                operators work in their own zone; the canonical UTC value
                is still on fx.kickoff if any future surface needs it. */}
            <Box sx={{ minWidth: 110, textAlign: "right", flexShrink: 0 }}>
              {fx.local?.time && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "baseline", gap: 0.75 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                    {fx.local.time}
                  </Typography>
                  <Typography sx={{ fontSize: 10, opacity: 0.55, letterSpacing: 0.4 }}>
                    {fx.local.tz}
                  </Typography>
                </Box>
              )}
              {fx.date && (
                <Typography sx={{ fontSize: 11, opacity: 0.5, mt: 0.25 }}>
                  {formatDateDDMMYYYY(fx.date)}
                </Typography>
              )}
            </Box>

            {/* VENUE: own column so it doesn't stack under the time and
                lose visual weight. */}
            <Box sx={{ minWidth: 160, maxWidth: 200, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  opacity: 0.75,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fx.location}
              </Typography>
            </Box>

            {/* SELECT */}
            <Box sx={{ flexShrink: 0 }}>
              <Button
                variant={isActive ? "outlined" : "contained"}
                onClick={() => onSelect(fx)}
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  minWidth: 130,
                  backgroundColor: isActive ? "transparent" : "#fff",
                  color: isActive ? "#00A36C" : "#000",
                  borderColor: isActive ? "#00A36C" : "transparent",
                  "&:hover": {
                    backgroundColor: isActive ? "transparent" : "#4CC6FF",
                  },
                }}
              >
                Select Fixture
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
